/**
 * Deployment environments and deployments (task 760).
 *
 * Report: `project/reports/innovate-service-deployment-environments.md`.
 *
 * The mental model is the container registry one: a published endpoint version
 * is the immutable image (its pin set is the lockfile), an **environment** is a
 * tag pointer, and deploying is repointing the tag. Promotion and rollback are
 * therefore the same operation — naming a version for an environment — which is
 * why there is one create call below and not three.
 *
 * These shapes mirror `innovate-service`'s own vocabulary
 * (`src/types/domain/environment.ts`, `src/processes/environment.ts`) so the app
 * never re-labels what the service already names. Always presented to Users as
 * a **deployment environment**: these are the Account's stages, not the
 * platform's own local/preprod/prd infrastructure.
 */

/**
 * The path of the environment that owns the BARE URL (task 830).
 *
 * `/` is not a segment — it is the statement that this environment has none:
 * `/api/{slug}/{endpointPath}` serves it, and that is its only address. At most
 * one live environment per Account holds it, which is the whole of what the
 * retired `isDefault` boolean used to say.
 */
export const BARE_URL_ENVIRONMENT_PATH = '/';

/** An Account-named deployment stage. */
export interface Environment {
  id: string;
  accountId?: string;
  /**
   * The DISPLAY label — free text since task 830. "Production (EU)" is a valid
   * name; nothing about it reaches a URL, so it carries no slug rules and two
   * stages may share one. Never use it to address an environment.
   */
  name: string;
  /**
   * The URL IDENTITY (task 830): a slug-safe segment, or `/` for the bare URL.
   * Unique per Account, and what every URL helper and every write that names an
   * environment — deploying included — must send.
   */
  path: string;
  /** The promotion sequence — staging before prod. Lanes render in this order. */
  order: number;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEnvironmentInput {
  name: string;
  path: string;
  order?: number;
  description?: string;
}

export type UpdateEnvironmentInput = Partial<CreateEnvironmentInput>;

/**
 * The published endpoint version an environment points at.
 *
 * Deployments are **append-only**: a row is never updated, so "what does staging
 * serve right now" is the newest live row for the pair — a query, not a column.
 * The same rows read in full are the rollout history.
 */
export interface Deployment {
  id: string;
  endpointId: string;
  environmentId: string;
  resourceVersionId: string;
  notes: string | null;
  createdAt: string;
  createdBy?: string | null;
  /** Joined by the service on both deployment reads. */
  environment?: Pick<Environment, 'id' | 'name' | 'path' | 'order'>;
  resourceVersion?: {
    id: string;
    major: number;
    minor: number;
    status: string;
    publishedAt: string | null;
  };
}

/**
 * Point an environment at a published version. Promote and roll back are the
 * SAME call — rolling back is naming an earlier version — so the UI's two
 * actions differ only in which version they pass and what they say first.
 */
export interface CreateDeploymentInput {
  /**
   * The environment PATH, e.g. `"prod"` or `"/"` — not its id, and not its
   * display label (task 830). The service resolves this field against
   * `Environment.path`, which is the only field that identifies an environment
   * now that names are free text.
   */
  environment: string;
  /** A published version of this AI Service, e.g. `"1.2"`. Drafts are refused. */
  version: string;
  notes?: string;
}

/**
 * THERE IS NO PER-SERVICE SERVING MODE (tasks 853, 869).
 *
 * A `DeploymentMode` type used to live here, describing a two-option
 * serving-mode field on the endpoint. Task 853 removed every app-side write and
 * read of it, task 869 removed the field from innovate-service's response and
 * its column from the database, and task 871 retired its two option names from
 * the app's vocabulary — so there is nothing left for the type to describe.
 *
 * Every "is this AI Service environment-served?" question is answered by the
 * ACCOUNT's publishing setting (`useVersioningEnabled` in
 * `hooks/useResourceVersions`): with Publishing off a service serves its
 * working state, and with Publishing on the environments are what serve the
 * released versions — the one holding `/` at the bare URL, each other at its
 * own segment.
 */

/** A version label as the service formats it (`major.minor`), e.g. `"1.2"`. */
export const versionLabel = (version: { major: number; minor: number }): string =>
  `${version.major}.${version.minor}`;

/**
 * Index the current deployments by environment id, so a lane can ask what it
 * serves without scanning. Deployment reads already return one row per
 * environment, newest first.
 */
export const deploymentsByEnvironment = (
  deployments: Deployment[]
): Record<string, Deployment> => {
  const byEnvironment: Record<string, Deployment> = {};
  for (const deployment of deployments) {
    if (!byEnvironment[deployment.environmentId]) {
      byEnvironment[deployment.environmentId] = deployment;
    }
  }
  return byEnvironment;
};
